import type { ChauffeurPage } from "../slug-chauffeur";
import { vehiclePriceFaq, vehicleSeo } from "./vehiclePricing";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";

const classeSAvecChauffeur: ChauffeurPage = {
  slug: "classe-s-avec-chauffeur",
  title: "Mercedes Classe S avec chauffeur — berline de prestige",
  subtitle:
    "La limousine de référence avec chauffeur privé : prestige, silence et confort lounge sur la Côte d'Azur.",
  keywords: [
    "Mercedes Classe S avec chauffeur",
    "berline de luxe avec chauffeur Cannes",
    "limousine avec chauffeur Côte d'Azur",
    "VTC luxe Nice",
  ],
  hero: {
    title: "Mercedes Classe S avec chauffeur sur la Côte d'Azur",
    subtitle:
      "La berline de prestige par excellence : confort lounge à l'arrière, silence absolu et chauffeur d'exception pour une clientèle exigeante.",
    keywords: ["Classe S avec chauffeur", "berline de luxe", "limousine"],
    imageSrc: heroImage.src,
    imageAlt: "Mercedes Classe S avec chauffeur privé à Cannes",
    badges: ["Jusqu'à 3 passagers", "Confort lounge", "Prestige"],
  },
  benefits: {
    heading: "Pourquoi la Classe S avec chauffeur KS Rent Services",
    items: [
      {
        title: "Prestige absolu",
        description:
          "La référence des berlines de luxe : une présence et un raffinement qui s'imposent, du tapis rouge au rendez-vous d'exception.",
      },
      {
        title: "Confort lounge",
        description:
          "Sièges arrière enveloppants, silence de roulement et finitions haut de gamme : un véritable salon en mouvement.",
      },
      {
        title: "Chauffeur d'exception",
        description:
          "Discrétion, anticipation et sens du détail : l'accueil et le service que mérite une clientèle exigeante.",
      },
      {
        title: "Arrivée remarquée",
        description:
          "Soignez chaque entrée en scène : la Classe S marque les esprits lors de vos événements et réceptions.",
      },
    ],
  },
  services: {
    heading: "Prestations de la berline de prestige",
    services: [
      {
        title: "Festival & tapis rouge",
        text: "Montées des marches, premières et événements : une arrivée à la hauteur de l'occasion.",
      },
      {
        title: "Soirées de gala & réceptions",
        text: "Dîners, galas et soirées privées sur toute la Côte d'Azur, avec chauffeur dédié à votre disposition.",
      },
      {
        title: "Transferts VIP",
        text: "Aéroport de Nice, héliport de Monaco, hôtels et résidences : transferts haut de gamme et confidentiels.",
      },
      {
        title: "Mariages & mise à disposition",
        text: "Cortège de prestige ou chauffeur à la journée pour vos plus belles occasions.",
      },
    ],
  },
  locations: {
    heading: "Zones desservies",
    items: [
      { name: "Cannes" },
      { name: "Monaco" },
      { name: "Nice & aéroport" },
      { name: "Saint-Tropez" },
      { name: "Antibes — Cap d'Antibes" },
    ],
  },
  generic: {
    heading: "Le sommet de la berline avec chauffeur",
    subheading: "Quand le trajet fait déjà partie de l'expérience.",
    paragraphs: [
      "La Mercedes Classe S incarne le luxe automobile : un habitacle pensé comme un lounge, où chaque détail concourt à votre confort et à votre sérénité.",
      "Pour vos événements, vos réceptions et vos déplacements VIP, elle conjugue prestige visible et discrétion absolue.",
      "Un interlocuteur unique, un chauffeur d'exception et la même exigence de ponctualité que sur l'ensemble de notre flotte.",
    ],
    bullets: [
      "Berline de prestige jusqu'à 3 passagers",
      "Forfaits aéroport transparents",
      "Disponibilité 24h/24, 7j/7",
    ],
  },
  process: {
    heading: "Comment réserver votre Classe S avec chauffeur",
    steps: [
      "Indiquez votre trajet, votre événement et vos horaires.",
      "Recevez un forfait clair ou une estimation au kilomètre.",
      "Affectation de la Classe S et de votre chauffeur d'exception.",
      "Prise en charge prestige, ponctuelle et confidentielle.",
    ],
  },
  faq: [
    {
      q: "Quel véhicule pour la berline de prestige ?",
      a: "Une Mercedes Classe S, la référence des berlines de luxe, pour jusqu'à 3 passagers, idéale pour les événements et les déplacements VIP.",
    },
    {
      q: "Quelle différence avec la Classe E ?",
      a: "La Classe S offre une expérience supérieure — confort lounge, prestige et présence — là où la Classe E reste la berline d'affaires au meilleur rapport prestation/prix.",
    },
    {
      q: "Convient-elle au Festival de Cannes et aux galas ?",
      a: "Absolument : c'est le véhicule des arrivées remarquées, des montées des marches et des soirées de prestige.",
    },
    vehiclePriceFaq("fr", "CLASS_S", "d'une Classe S avec chauffeur"),
    {
      q: "Comment réserver ?",
      a: "Indiquez-nous votre trajet et votre occasion : nous confirmons rapidement la Classe S et votre chauffeur dédié.",
    },
  ],
  relatedLinks: [
    {
      label: "Berline avec chauffeur (Classe E)",
      href: "/chauffeur/berline-avec-chauffeur",
    },
    {
      label: "Range Rover avec chauffeur (SUV)",
      href: "/chauffeur/suv-avec-chauffeur",
    },
    { label: "Tous nos services chauffeur", href: "/chauffeur" },
    {
      label: "Voiture avec chauffeur pour un mariage",
      href: "/chauffeur/voiture-mariage-cannes",
    },
  ],
  cta: {
    title: "Une occasion d'exception à honorer ?",
    text: "Dites-nous votre trajet — devis rapide pour votre Mercedes Classe S avec chauffeur.",
    primaryHref: "/contact",
    primaryLabel: "Demander un devis",
  },
  seo: {
    ...vehicleSeo(
      "fr",
      "CLASS_S",
      "Mercedes Classe S avec chauffeur, Côte d'Azur",
      "Mercedes Classe S avec chauffeur",
    ),
    canonical: "/chauffeur/classe-s-avec-chauffeur",
  },
};

export default classeSAvecChauffeur;
